import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useTheme } from '@mui/material';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

import { forceString, isDateAfterToday } from 'src/utils/datetime';

import messages from './messages';

interface Props {
  certificate: {
    id: string;
    status: {
      code: string;
      id: string;
      name: string;
      uuid: string | undefined;
    };
    uuid: string;
    validFrom: string | null;
    validUntil: string | null;
  };
  index: number;
}

export default function CandidateHealthCertificate({
  certificate,
  index,
}: Props) {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const isValid = useMemo(() => {
    if (certificate?.validUntil) {
      return isDateAfterToday(certificate?.validUntil);
    }
  }, [certificate?.validUntil]);

  const certificateStyleSx = useMemo(() => {
    return {
      opacity: isValid ? 1 : 0.38,
    };
  }, [isValid]);

  return (
    <Box
      p={2}
      borderColor={theme.palette.grey[500]}
      border="1px solid"
      borderRadius={1.5}
      width="fit-content"
      sx={certificateStyleSx}
    >
      <Stack direction="row" mb={1} justifyContent="space-between">
        <Typography variant="h5">
          {formatMessage(messages.certificate, { index: index + 1 })}
        </Typography>
        <Chip
          size="small"
          label={
            isValid
              ? formatMessage(messages.active)
              : formatMessage(messages.expired)
          }
          color={isValid ? 'success' : 'error'}
        />
      </Stack>
      <Stack direction="row" spacing={6}>
        <Stack spacing={1}>
          <Typography variant="h6" color={theme.palette.grey[500]}>
            {formatMessage(messages.validFrom)}
          </Typography>
          <Typography variant="h6">
            {forceString(certificate?.validFrom)}
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <Typography variant="h6" color={theme.palette.grey[500]}>
            {formatMessage(messages.validUntil)}
          </Typography>
          <Typography variant="h6">
            {forceString(certificate?.validUntil)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
